import { createBigDecimal } from '@0xtorch/big-decimal'
import type { CryptoCurrency, Nft, Transfer } from '@0xtorch/core'
import { solana } from '@0xtorch/core'
import { nativeTokenDecimals } from '../../constants'
import type { SplTokenInfo } from '../../splTokenDatasource'
import { createSolanaAddressId } from '../../utils'
import type { GeneratorParameters, TokenTransfer } from '../types'

export const generateTransfer = ({
  tokenTransfers,
  tokenAccounts,
  tokenAccountOwnerMap,
  splTokens,
  accountIds,
}: GeneratorParameters & {
  readonly splTokens: readonly SplTokenInfo[]
}): readonly Transfer[] | undefined => {
  const accountIdSet = new Set<string | undefined>(accountIds)

  const transfers: Transfer[] = []
  for (const transfer of tokenTransfers) {
    const { asset, decimals } = getAssetNDecimalOfTokenTransfer(
      transfer,
      splTokens,
    )
    const from =
      transfer.type === 'native' || transfer.from === undefined
        ? transfer.from
        : (tokenAccounts[transfer.from]?.owner ??
          tokenAccountOwnerMap[transfer.from] ??
          transfer.from)
    const to =
      transfer.type === 'native' || transfer.to === undefined
        ? transfer.to
        : (tokenAccounts[transfer.to]?.owner ??
          tokenAccountOwnerMap[transfer.to] ??
          transfer.to)
    const fromId =
      from === undefined ? undefined : createSolanaAddressId({ address: from })
    const toId =
      to === undefined ? undefined : createSolanaAddressId({ address: to })
    if (!accountIdSet.has(fromId) && !accountIdSet.has(toId)) {
      continue
    }
    let direction: Transfer['direction']
    if (accountIdSet.has(fromId) && !accountIdSet.has(toId)) {
      direction = 'out'
    } else if (!accountIdSet.has(fromId) && accountIdSet.has(toId)) {
      direction = 'in'
    } else {
      direction = 'none'
    }
    transfers.push({
      direction,
      asset,
      amount: createBigDecimal(transfer.amount, decimals),
      from: fromId,
      to: toId,
    })
  }
  return transfers
}

const getAssetNDecimalOfTokenTransfer = (
  transfer: TokenTransfer,
  splTokens: readonly SplTokenInfo[],
): {
  readonly asset: CryptoCurrency | Nft
  readonly decimals: number
} => {
  switch (transfer.type) {
    case 'native': {
      return {
        asset: solana,
        decimals: nativeTokenDecimals,
      }
    }
    case 'spl': {
      const splToken = splTokens.find(
        (splToken) => splToken.address === transfer.token,
      )
      if (splToken === undefined) {
        // Unknown token fallback (burned or unavailable tokens)
        return {
          asset: {
            type: 'CryptoCurrency' as const,
            id: `SolanaToken/${transfer.token}`,
            name: transfer.token,
            symbol: '',
            icon: undefined,
            market: {
              coingeckoId: undefined,
              marketCapUsd: undefined,
            },
            priceDatasourceId: undefined,
            updatedAt: 0,
          },
          decimals: 0,
        }
      }
      return {
        asset: splToken.asset,
        decimals: splToken.decimals,
      }
    }
    default: {
      throw new Error(`Unknown transfer type: ${transfer satisfies never}`)
    }
  }
}
